import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  IFilter,
  IPopulate,
  IQueryParam,
  ISirena as ISirenaG,
  ILayerMapaPersonalizado,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { ISirena } from 'modelosSeguridad/src';
import { MapaFacilV2Service } from '../../../../auxiliares/mapbox/mapa-facil-v2.service';
import { LoginService } from '../../../login/login.service';
import { SirenasLayerManager } from '../../../../auxiliares/mapbox/entity-managers/sirenas-layer.manager';
import { EntityManagerRegistry } from '../../../../auxiliares/mapbox/core/registry/entity-manager-registry.service';
import { MatDialog } from '@angular/material/dialog';

export interface SirenaMapa extends ISirenaG {
  sirena?: ISirena;
}

@Component({
  selector: 'app-mapa-sirenas',
  templateUrl: './mapa-sirenas.component.html',
  styleUrl: './mapa-sirenas.component.scss',
  standalone: false,
})
export class MapaSirenasComponent implements OnInit, OnDestroy {
  public loading = signal(false);
  public sirenasG?: SirenaMapa[] = [];
  public sirenasFiltradas?: SirenaMapa[];
  public map: mapboxgl.Map | null = null;
  public mapasPersonalizados: ILayerMapaPersonalizado[] = [];
  //Subs
  public sirenasG$?: Subscription;
  public sirenas$?: Subscription;
  private dialogClose$?: Subscription;

  // Entidades permitidas para re-aplicar datos al cambiar capa base. Las entidades pueden ser las que están presentes en MapaFacilV2Data
  public allowedTypes: string[] = ['sirenas'];

  //
  public helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private mapaFacilV2Service = inject(MapaFacilV2Service);
  private entityRegistry = inject(EntityManagerRegistry);
  private sirenasLayerManager = inject(SirenasLayerManager);
  private dialog = inject(MatDialog);
  //
  // Clusterizado
  public mostrarClusters = false; // Por defecto las sirenas están desclusterizadas

  //
  public filter: IFilter<ISirenaG> = {};
  public populate: IPopulate[] = [
    {
      path: 'cliente',
      select: 'nombre',
    },
    {
      path: 'camaras',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: 'fechaCreacion',
    includeChildren: true,
    unlimited: true,
  };

  /**
   * Lista sirenasG una sola vez (sin intervalo)
   */
  public async listarSirenasG(): Promise<void> {
    this.loading.set(true);
    this.sirenasG$?.unsubscribe();
    this.sirenasG$ = this.lista2
      .sirenasG(this.query, 0) // Intervalo 0 = una sola vez
      .subscribe(async (data) => {
        this.sirenasG = data.datos;
        // Después de cargar sirenasG, iniciar el polling de sirenas
        await this.iniciarActualizacionSirenas();
      });
  }

  /**
   * Inicia la actualización periódica de sirenas cada 60 segundos
   */
  private async iniciarActualizacionSirenas(): Promise<void> {
    const query = {
      select:
        'chipId direccionGps geojson fechaOnline fechaOffline online activa sonidoEncendido luzEncendida errorActualizacion actualizando versionFirmware',
      includeChildren: true,
    };

    this.sirenas$?.unsubscribe();
    // Usar interval de 60 segundos para actualizar solo los estados
    this.sirenas$ = this.lista2.sirenas(query, 60).subscribe((response) => {
      if (!response) {
        this.loading.set(false);
        return;
      }
      const sirenasIntegracionesById = new Map<string, ISirena>();
      const sirenasIntegracionesByChipId = new Map<string, ISirena>();

      for (const sirena of response.datos || []) {
        if (sirena._id) {
          sirenasIntegracionesById.set(sirena._id, sirena);
        }
        if (sirena.chipId) {
          sirenasIntegracionesByChipId.set(sirena.chipId, sirena);
        }
      }

      // Actualizar los datos de sirena en sirenasG creando una nueva referencia
      this.sirenasG = this.sirenasG?.map((sirenaG) => ({
        ...sirenaG,
        sirena:
          (sirenaG.idExterno
            ? sirenasIntegracionesById.get(sirenaG.idExterno)
            : undefined) ||
          (sirenaG.chipId
            ? sirenasIntegracionesByChipId.get(sirenaG.chipId)
            : undefined) ||
          sirenaG.sirena,
      }));

      this.redrawCurrentSelection();

      // Ocultar loading después de la primera carga
      this.loading.set(false);
    });
  }

  private isValidCoordinates(value: unknown): value is [number, number] {
    return (
      Array.isArray(value) &&
      value.length >= 2 &&
      typeof value[0] === 'number' &&
      typeof value[1] === 'number' &&
      Number.isFinite(value[0]) &&
      Number.isFinite(value[1])
    );
  }

  private getSirenaCoordinates(sg: SirenaMapa): [number, number] | null {
    const coordenadasIntegraciones = (sg.sirena as any)?.geojson?.coordinates;
    if (this.isValidCoordinates(coordenadasIntegraciones)) {
      return [coordenadasIntegraciones[0], coordenadasIntegraciones[1]];
    }

    const ubicacionGpsIntegraciones = (sg.sirena as any)?.ubicacionGps;
    if (
      ubicacionGpsIntegraciones &&
      Number.isFinite(ubicacionGpsIntegraciones.lng) &&
      Number.isFinite(ubicacionGpsIntegraciones.lat)
    ) {
      return [ubicacionGpsIntegraciones.lng, ubicacionGpsIntegraciones.lat];
    }

    const coordenadasGestion = sg.ubicacion?.coordinates;
    if (this.isValidCoordinates(coordenadasGestion)) {
      return [coordenadasGestion[0], coordenadasGestion[1]];
    }

    return null;
  }

  private buildMarkerData(sg: SirenaMapa, withPopupData = false): any | null {
    const coordenadas = this.getSirenaCoordinates(sg);
    if (!coordenadas) return null;

    const onlineFallback =
      sg.estado === 'Online'
        ? true
        : sg.estado === 'Offline'
          ? false
          : undefined;
    const onlineValue = sg.sirena?.online ?? onlineFallback;
    const sonidoEncendido = sg.sirena?.sonidoEncendido;
    const luzEncendida = sg.sirena?.luzEncendida;
    const estadoNormalizado =
      onlineValue === true
        ? sonidoEncendido || luzEncendida
          ? 'encendida'
          : 'online'
        : onlineValue === false
          ? 'offline'
          : undefined;

    const markerData: any = {
      // Priorizar estado y datos técnicos de Integraciones
      ...sg,
      ...sg.sirena,

      // Mantener IDs principales de Gestión para navegación/acciones
      _id: sg._id,
      idCliente: sg.idCliente,
      idExterno: sg.idExterno,
      idsAsignados: sg.idsAsignados,
      coordenadas,

      // Garantizar campos de estado para colores/filtros
      online: onlineValue,
      estado: estadoNormalizado,
      chipId: sg.sirena?.chipId ?? sg.chipId,
      activa: sg.sirena?.activa ?? sg.activa,
    };

    if (!withPopupData) {
      return markerData;
    }

    return {
      ...markerData,
      popupData: {
        _id: sg._id,
        nombre: (sg as any).nombre,
        idExterno: sg.idExterno,
        idCliente: sg.idCliente,
        idsAsignados: sg.idsAsignados,
        sirena: sg.sirena,
      },
    };
  }

  private addMarkersSirenas() {
    if (!this.mapaFacilV2Service.isReady()) return;

    const sirenas = (this.sirenasG || []).reduce<any[]>((acc, sg) => {
      const markerData = this.buildMarkerData(sg, true);
      if (markerData) {
        acc.push(markerData);
      }
      return acc;
    }, []);

    this.mapaFacilV2Service.updateData({ sirenas: sirenas });
  }

  private redrawCurrentSelection(): void {
    // Si hay un subconjunto filtrado activo, mantener esa selección en el mapa.
    // Si no, actualizar todos los marcadores.
    if (this.sirenasFiltradas !== undefined) {
      const idsFiltrados = new Set(
        (this.sirenasFiltradas || []).map((sirena) => sirena._id),
      );
      const sirenasFiltradasActualizadas = (this.sirenasG || []).filter(
        (sirena) => idsFiltrados.has(sirena._id),
      );
      this.onSirenaChange(sirenasFiltradasActualizadas);
      return;
    }

    this.addMarkersSirenas();
  }

  //Procesamiento de lo guardado en el local storage
  private getBooleanFromStorage(key: string, defaultValue: boolean): boolean {
    const value = localStorage.getItem(key);
    return value !== null ? value === 'true' : defaultValue;
  }

  public onMapReady(map: mapboxgl.Map): void {
    // Mapa específico de sirenas: usar iconos por estado)
    this.sirenasLayerManager.setMapaEspecifico(true);

    // Habilitar/deshabilitar clustering según estado guardado
    if (this.mostrarClusters) {
      this.mapaFacilV2Service.setClusteringEnabled('sirenas', true);
    }
    this.map = map;
    this.addMarkersSirenas();

    // Cuando un dialog (ej: detalles de sirena) se cierra, el mini-mapa destruye el
    // estado del servicio singleton. Restauramos el mapa de fondo y re-dibujamos.
    this.dialogClose$ = this.dialog.afterAllClosed.subscribe(() => {
      if (this.map) {
        this.mapaFacilV2Service.restore(this.map);
      }

      // Restaurar configuración visual de sirenas al volver de dialogs con mini-mapa
      this.sirenasLayerManager.setMapaEspecifico(true);
      this.mapaFacilV2Service.setClusteringEnabled(
        'sirenas',
        this.mostrarClusters,
      );

      this.redrawCurrentSelection();
    });
  }

  async ngOnInit() {
    // Cargar mapas personalizados del cliente
    const clientePropio = LoginService.getClientePropio();
    if (clientePropio?.mapLayers) {
      this.mapasPersonalizados = clientePropio.mapLayers;
    }

    this.mostrarClusters = this.getBooleanFromStorage(
      'mostrarClusterSirena',
      false,
    );

    await this.listarSirenasG();
  }

  public onSirenaChange(sirenas: SirenaMapa[]): void {
    this.sirenasFiltradas = sirenas;

    if (!this.mapaFacilV2Service.isReady()) return;

    const sirenasExtendidas = (sirenas || []).reduce<any[]>((acc, sg) => {
      const markerData = this.buildMarkerData(sg, true);
      if (markerData) {
        acc.push(markerData);
      }
      return acc;
    }, []);

    // El manager de sirenas escucha la clave 'sirenas'.
    this.mapaFacilV2Service.updateData({ sirenas: sirenasExtendidas });
  }

  public onClusterToggle(enabled: boolean): void {
    this.mostrarClusters = enabled;
    this.mapaFacilV2Service.setClusteringEnabled(
      'sirenas',
      this.mostrarClusters,
    );
    // Aquí puedes implementar la lógica de clustering si es necesario
  }

  ngOnDestroy() {
    this.sirenasG$?.unsubscribe();
    this.sirenas$?.unsubscribe();
    this.dialogClose$?.unsubscribe();

    // IMPORTANTE: Limpiar datos del servicio para evitar que persistan al cambiar de mapa
    // El servicio es singleton, por lo que mantiene datos entre componentes
    this.mapaFacilV2Service.clearAllData();

    // Restablecer el modo específico en managers de sirenas para no afectar otros mapas
    const sirenasManager = this.entityRegistry.get('sirenas') as any;
    if (
      sirenasManager &&
      typeof sirenasManager.setMapaEspecifico === 'function'
    ) {
      sirenasManager.setMapaEspecifico(false);
    }
  }
}
